"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useAgentChat = useAgentChat;
var _react = require("react");
/**
 * Hook for managing AI agent chat state (messages + usage counters).
 *
 * @param {object} opts
 * @param {string|null} opts.uid               Authenticated user UID.
 * @param {function}    [opts.loadUsage]       async (uid) => usageObject. Called on uid change.
 * @param {function}    [opts.onFirstReply]    Called after the first successful reply in the session.
 * @returns {{ messages, usage, onNewMessage, onReply, onCondense, onClear }}
 */
function useAgentChat({
  uid,
  loadUsage,
  onFirstReply
} = {}) {
  const [messages, setMessages] = (0, _react.useState)([]);
  const [usage, setUsage] = (0, _react.useState)(null);
  (0, _react.useEffect)(() => {
    setMessages([]);
    setUsage(null);
    if (!uid || !loadUsage) return;
    loadUsage(uid).then(u => setUsage(u)).catch(() => {});
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const onNewMessage = msg => setMessages(prev => [...prev, msg]);
  const onReply = data => {
    if (data.condensedHistory) {
      const [marker, summary, ...rest] = data.condensedHistory;
      setMessages([...rest, {
        ...marker,
        condensed: true
      }, {
        ...summary,
        condensed: true
      }, {
        role: 'user',
        content: data.userMsg
      }, {
        role: 'assistant',
        content: data.reply
      }]);
    }
    if (onFirstReply) onFirstReply();
    setUsage(prev => ({
      ...data,
      convTokensIn: (prev?.convTokensIn ?? 0) + (data.tokensIn ?? 0),
      convTokensOut: (prev?.convTokensOut ?? 0) + (data.tokensOut ?? 0)
    }));
  };
  const onCondense = (condensedHistory, tokensBefore) => {
    const [marker, summary, ...rest] = condensedHistory;
    setMessages([...rest, {
      ...marker,
      condensed: true
    }, {
      ...summary,
      condensed: true
    }]);
    if (tokensBefore != null) {
      setUsage(prev => prev ? {
        ...prev,
        condenseTokensBefore: tokensBefore,
        condenseTokensAfter: null
      } : null);
    }
  };
  const onClear = () => {
    setMessages([]);
    setUsage(prev => prev ? {
      ...prev,
      convTokensIn: 0,
      convTokensOut: 0
    } : null);
  };
  return {
    messages,
    usage,
    onNewMessage,
    onReply,
    onCondense,
    onClear
  };
}